import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, BuildingIcon, CalendarIcon, CreditCardIcon, FileTextIcon } from 'lucide-react';
import { Settlement } from '../types/Transaction';
export function SettlementDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data
  const settlement: Settlement = {
    id: id || '1',
    settlementId: 'SETTLE-2024-001',
    amount: 145000,
    currency: 'LKR',
    status: 'completed',
    date: '2024-01-15T09:00:00',
    payoutDate: '2024-01-17T14:30:00',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    bankName: 'DFCC Bank',
    bankAccountNumber: '****1234',
    bankAccountName: 'ABC Store Pvt Ltd',
    branchCode: '001',
    transactionCount: 45,
    transactionIds: ['TXN-2024-001234', 'TXN-2024-001235', 'TXN-2024-001236', 'TXN-2024-001237', 'TXN-2024-001238'],
    fee: 4350,
    netAmount: 140650,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created with 45 transactions',
      metadata: {
        transaction_count: '45',
        total_amount: 'Rs. 145,000'
      }
    }, {
      id: '2',
      timestamp: '2024-01-16T10:00:00',
      status: 'processing',
      description: 'Processing payout to merchant bank account',
      metadata: {
        bank: 'DFCC Bank',
        account: '****1234'
      }
    }, {
      id: '3',
      timestamp: '2024-01-17T14:30:00',
      status: 'completed',
      description: 'Funds successfully transferred to merchant account',
      metadata: {
        reference_number: 'REF123456789',
        net_amount: 'Rs. 140,650'
      }
    }]
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      reversed: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-5413">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  return <div className="space-y-6" data-id="element-5414">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5415">
        <button onClick={() => navigate('/settlements')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-5416">
          <ArrowLeftIcon size={20} data-id="element-5417" />
          Back to Settlements
        </button>
        <div className="flex items-center gap-3" data-id="element-5418">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5419">
            <DownloadIcon size={18} data-id="element-5420" />
            Download Statement
          </button>
        </div>
      </div>
      {/* Settlement Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5421">
        <div className="flex items-start justify-between mb-6" data-id="element-5422">
          <div data-id="element-5423">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5424">
              {settlement.settlementId}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-5425">
              Settlement for {settlement.merchantName}
            </p>
          </div>
          {getStatusBadge(settlement.status)}
        </div>
        {/* Amount Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 pb-6 border-b border-gray-200" data-id="element-5426">
          <div data-id="element-5427">
            <div className="text-sm text-gray-500" data-id="element-5428">Gross Amount</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5429">
              Rs. {settlement.amount.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5430">
            <div className="text-sm text-gray-500" data-id="element-5431">Processing Fee</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5432">
              Rs. {settlement.fee.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5433">
            <div className="text-sm text-gray-500" data-id="element-5434">Net Amount</div>
            <div className="mt-1 text-2xl font-semibold text-green-600" data-id="element-5435">
              Rs. {settlement.netAmount.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5436">
            <div className="text-sm text-gray-500" data-id="element-5437">Transactions</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5438">
              {settlement.transactionCount}
            </div>
          </div>
        </div>
        {/* Settlement Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5439">
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-5440">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5441">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-5442" />
              Merchant Information
            </h3>
            <div className="space-y-3" data-id="element-5443">
              <div data-id="element-5444">
                <div className="text-sm text-gray-500" data-id="element-5445">Merchant Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5446">
                  {settlement.merchantName}
                </div>
              </div>
              <div data-id="element-5447">
                <div className="text-sm text-gray-500" data-id="element-5448">Merchant ID</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5449">
                  {settlement.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Bank Information */}
          <div className="space-y-4" data-id="element-5450">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5451">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-5452" />
              Bank Account Information
            </h3>
            <div className="space-y-3" data-id="element-5453">
              <div data-id="element-5454">
                <div className="text-sm text-gray-500" data-id="element-5455">Bank Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5456">
                  {settlement.bankName}
                </div>
              </div>
              <div data-id="element-5457">
                <div className="text-sm text-gray-500" data-id="element-5458">Account Number</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5459">
                  {settlement.bankAccountNumber}
                </div>
              </div>
              <div data-id="element-5460">
                <div className="text-sm text-gray-500" data-id="element-5461">Account Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5462">
                  {settlement.bankAccountName}
                </div>
              </div>
              <div data-id="element-5463">
                <div className="text-sm text-gray-500" data-id="element-5464">Branch Code</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5465">
                  {settlement.branchCode}
                </div>
              </div>
            </div>
          </div>
          {/* Date Information */}
          <div className="space-y-4" data-id="element-5466">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5467">
              <CalendarIcon size={20} className="text-blue-600" data-id="element-5468" />
              Timeline
            </h3>
            <div className="space-y-3" data-id="element-5469">
              <div data-id="element-5470">
                <div className="text-sm text-gray-500" data-id="element-5471">Settlement Date</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5472">
                  {new Date(settlement.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              {settlement.payoutDate && <div data-id="element-5473">
                  <div className="text-sm text-gray-500" data-id="element-5474">Payout Date</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-5475">
                    {new Date(settlement.payoutDate).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Included Transactions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5476">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2" data-id="element-5477">
          <FileTextIcon size={20} className="text-blue-600" data-id="element-5478" />
          Included Transactions ({settlement.transactionCount})
        </h2>
        <div className="space-y-2" data-id="element-5479">
          {settlement.transactionIds.map(txnId => <div key={txnId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100" data-id="element-5480">
              <span className="text-sm font-medium text-gray-900" data-id="element-5481">{txnId}</span>
              <button onClick={() => navigate(`/sale-detail/${txnId}`)} className="text-sm text-blue-600 hover:text-blue-900" data-id="element-5482">
                View Details
              </button>
            </div>)}
        </div>
      </div>
      {/* Settlement Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5483">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-5484">
          Settlement Lifecycle
        </h2>
        <div className="space-y-6" data-id="element-5485">
          {settlement.lifecycle.map((event, index) => <div key={event.id} className="relative" data-id="element-5486">
              {index !== settlement.lifecycle.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-5487"></div>}
              <div className="flex items-start gap-4" data-id="element-5488">
                <div className="flex-shrink-0" data-id="element-5489">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-5490">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-5491"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-5492">
                  <div className="flex items-center justify-between" data-id="element-5493">
                    <div className="flex items-center gap-3" data-id="element-5494">
                      <p className="text-sm font-medium text-gray-900" data-id="element-5495">
                        {event.description}
                      </p>
                      {getStatusBadge(event.status)}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-5496">
                      {new Date(event.timestamp).toLocaleString('en-GB', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                    </p>
                  </div>
                  {event.metadata && <div className="mt-2 p-3 bg-gray-50 rounded-lg" data-id="element-5497">
                      <div className="grid grid-cols-2 gap-2" data-id="element-5498">
                        {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-5499">
                            <span className="text-xs text-gray-500" data-id="element-5500">
                              {key.replace('_', ' ').toUpperCase()}:
                            </span>
                            <span className="text-xs font-medium text-gray-900 ml-1" data-id="element-5501">
                              {String(value)}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}